import type { RectMm } from '../layout/doorLayout';

/** Full-door meet-fit. */
export const PREVIEW_FIT_SCALE = 1;
/**
 * First click/tap zoom, relative to the largest canvas-shaped window that
 * fits in the door. Milder than 1.12; the clip still fills the preview pane.
 */
export const PREVIEW_CLICK_SCALE = 1.08;
export const PREVIEW_MAX_SCALE = 4;
export const PREVIEW_PAN_THRESHOLD_PX = 6;
/** Used when the preview pane has not been measured yet. Wider than a typical door. */
export const PREVIEW_FALLBACK_VIEWPORT_ASPECT = 4 / 5;

export function formatViewBox(box: RectMm): string {
  const n = (value: number) => {
    const rounded = Number(value.toFixed(4));
    return Object.is(rounded, -0) ? 0 : rounded;
  };
  return `${n(box.x)} ${n(box.y)} ${n(box.width)} ${n(box.height)}`;
}

export function isFitScale(scale: number): boolean {
  return scale <= PREVIEW_FIT_SCALE + 1e-6;
}

export function clampPreviewScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= PREVIEW_FIT_SCALE) return PREVIEW_FIT_SCALE;
  return Math.min(PREVIEW_MAX_SCALE, scale);
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

export function worldCentre(world: RectMm): { x: number; y: number } {
  return { x: world.x + world.width / 2, y: world.y + world.height / 2 };
}

export function resolvedViewportAspect(viewportAspect: number | undefined, world: RectMm): number {
  if (viewportAspect && Number.isFinite(viewportAspect) && viewportAspect > 0.05) {
    return viewportAspect;
  }
  return PREVIEW_FALLBACK_VIEWPORT_ASPECT > 0
    ? PREVIEW_FALLBACK_VIEWPORT_ASPECT
    : world.width / world.height;
}

/**
 * Largest rectangle of `aspect` (width/height) that fits in `world`, then
 * divided by `scale`. Used while zoomed so the clip matches the preview pane
 * instead of the tall door silhouette.
 */
export function zoomWindowSize(
  world: RectMm,
  scale: number,
  viewportAspect?: number
): { width: number; height: number } {
  const aspect = resolvedViewportAspect(viewportAspect, world);
  const s = Math.max(clampPreviewScale(scale), PREVIEW_FIT_SCALE);
  let width: number;
  let height: number;
  if (world.width / world.height >= aspect) {
    height = world.height;
    width = height * aspect;
  } else {
    width = world.width;
    height = width / aspect;
  }
  return { width: width / s, height: height / s };
}

/**
 * Visible millimetre window. Fit uses the full door. Zoomed windows follow the
 * preview pane aspect so the clip is not a thin door-shaped slice.
 */
export function viewBoxForCamera(
  world: RectMm,
  scale: number,
  focusX: number,
  focusY: number,
  viewportAspect?: number
): RectMm {
  const nextScale = clampPreviewScale(scale);
  if (isFitScale(nextScale)) {
    return { x: world.x, y: world.y, width: world.width, height: world.height };
  }
  const { width, height } = zoomWindowSize(world, nextScale, viewportAspect);
  return {
    x: clamp(focusX - width / 2, world.x, world.x + world.width - width),
    y: clamp(focusY - height / 2, world.y, world.y + world.height - height),
    width,
    height,
  };
}

export function toggleClickScale(scale: number): number {
  return isFitScale(scale) ? PREVIEW_CLICK_SCALE : PREVIEW_FIT_SCALE;
}

/**
 * Keep `anchor` (user-space mm) at the same position in the viewport after a
 * scale change, then clamp inside `world`.
 */
export function focusForZoomToward(
  world: RectMm,
  current: RectMm,
  nextScale: number,
  anchor: { x: number; y: number },
  viewportAspect?: number
): { x: number; y: number } {
  const scale = clampPreviewScale(nextScale);
  if (isFitScale(scale)) return worldCentre(world);
  const { width, height } = zoomWindowSize(world, scale, viewportAspect);
  const relX = current.width > 0 ? (anchor.x - current.x) / current.width : 0.5;
  const relY = current.height > 0 ? (anchor.y - current.y) / current.height : 0.5;
  const x = clamp(anchor.x - relX * width, world.x, world.x + world.width - width);
  const y = clamp(anchor.y - relY * height, world.y, world.y + world.height - height);
  return { x: x + width / 2, y: y + height / 2 };
}

export function remapFocusToWorld(
  previous: RectMm,
  next: RectMm,
  focus: { x: number; y: number }
): { x: number; y: number } {
  if (previous.width <= 0 || previous.height <= 0) return worldCentre(next);
  return {
    x: next.x + ((focus.x - previous.x) / previous.width) * next.width,
    y: next.y + ((focus.y - previous.y) / previous.height) * next.height,
  };
}

export function wheelScaleFactor(deltaY: number, deltaMode = 0): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * window.innerHeight : deltaY;
  return Math.exp(-pixels * 0.00175);
}

export function parseSvgViewBox(svg: SVGSVGElement): RectMm {
  const base = svg.viewBox?.baseVal;
  if (base && base.width > 0 && base.height > 0) {
    return { x: base.x, y: base.y, width: base.width, height: base.height };
  }
  const parts = (svg.getAttribute('viewBox') ?? '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
  }
  return {
    x: 0,
    y: 0,
    width: Number(svg.getAttribute('width')) || 0,
    height: Number(svg.getAttribute('height')) || 0,
  };
}

/**
 * Map a pointer to millimetre user-space. Prefers the mirrored scene group's
 * CTM so internal view clicks zoom the visible point.
 */
export function clientToSvgUser(svg: SVGSVGElement, clientX: number, clientY: number): {
  x: number;
  y: number;
} {
  const scene = svg.querySelector('[data-part="door-view"]') as SVGGraphicsElement | null;
  const target = scene ?? svg;
  const ctm = typeof target.getScreenCTM === 'function' ? target.getScreenCTM() : null;
  if (ctm && typeof svg.createSVGPoint === 'function') {
    try {
      const point = svg.createSVGPoint();
      point.x = clientX;
      point.y = clientY;
      const mapped = point.matrixTransform(ctm.inverse());
      if (Number.isFinite(mapped.x) && Number.isFinite(mapped.y)) {
        return { x: mapped.x, y: mapped.y };
      }
    } catch {
      /* use the meet-fit fallback */
    }
  }

  const rect = svg.getBoundingClientRect();
  const viewBox = parseSvgViewBox(svg);
  if (rect.width <= 0 || rect.height <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
    return { x: viewBox.x + viewBox.width / 2, y: viewBox.y + viewBox.height / 2 };
  }
  const scale = Math.min(rect.width / viewBox.width, rect.height / viewBox.height);
  const contentWidth = viewBox.width * scale;
  const contentHeight = viewBox.height * scale;
  const originX = rect.left + (rect.width - contentWidth) / 2;
  const originY = rect.top + (rect.height - contentHeight) / 2;
  return {
    x: viewBox.x + (clientX - originX) / scale,
    y: viewBox.y + (clientY - originY) / scale,
  };
}

export function screenDeltaToUser(
  svg: SVGSVGElement,
  dx: number,
  dy: number
): { x: number; y: number } {
  const scene = svg.querySelector('[data-part="door-view"]') as SVGGraphicsElement | null;
  const ctm = (scene ?? svg).getScreenCTM?.() ?? null;
  if (ctm && Math.abs(ctm.a) > 1e-6 && Math.abs(ctm.d) > 1e-6) {
    return { x: dx / ctm.a, y: dy / ctm.d };
  }
  const rect = svg.getBoundingClientRect();
  const viewBox = parseSvgViewBox(svg);
  if (rect.width <= 0 || rect.height <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
    return { x: 0, y: 0 };
  }
  const scale = Math.min(rect.width / viewBox.width, rect.height / viewBox.height);
  return { x: dx / scale, y: dy / scale };
}
